import Component from "@base/Component.js";
import api from "@api/api.js";
import t from "@/services/translationService.js";
import authService from "@api/authService.js";

/**
 * Add/edit user modal. Mount to document.body.
 *
 * Deleting an account happens in here, not on the table row — same as every
 * other editor in the Cockpit, so a destructive action always sits behind
 * opening the thing first. You cannot delete the account you are signed in as,
 * which is why the button is absent rather than disabled for your own row.
 *
 * props: { user: object|null, onSaved() }  — user=null → create.
 */
export default class UserEditModal extends Component {
  render() {
    const u = this.props.user;
    const editing = Boolean(u);
    return `
      <div class="modal-backdrop">
        <div class="modal-card modal-card--editor" role="dialog" aria-modal="true" aria-label="${editing ? t.t("common.edit") : t.t("cockpit.users.add")}">
          <div class="modal-header">
            <h2>${editing ? t.t("common.edit") : t.t("cockpit.users.add")}</h2>
            <button type="button" class="modal-close" aria-label="${t.t("common.cancel")}">&times;</button>
          </div>
          <form class="login-form user-form" novalidate>
            <label>${t.t("cockpit.users.name")}
              <input name="name" required value="${u ? Component.escape(u.name) : ""}" />
            </label>
            <label>${t.t("cockpit.users.email")}
              <input type="email" name="email" autocomplete="off" required value="${u ? Component.escape(u.email) : ""}" />
            </label>
            <label>${t.t("cockpit.users.password")}
              ${editing ? `<span class="user-form__hint">${t.t("cockpit.users.passwordHint")}</span>` : ""}
              <input type="password" name="password" autocomplete="new-password" minlength="12" ${editing ? "" : "required"} />
            </label>
            <label>${t.t("cockpit.users.role")}
              <select name="admin">
                <option value="false" ${u && u.admin ? "" : "selected"}>${t.t("cockpit.users.roleUser")}</option>
                <option value="true" ${u && u.admin ? "selected" : ""}>${t.t("cockpit.users.roleAdmin")}</option>
              </select>
            </label>
            <div class="form-error" role="alert" hidden></div>
            <div class="editor-actions">
              <button type="submit" class="btn-primary">${t.t("common.save")}</button>
              ${
                editing && u.userId !== authService.user?.userId
                  ? `<button type="button" class="btn-danger" data-action="delete">${t.t("common.delete")}</button>`
                  : ""
              }
              <button type="button" class="btn-secondary" data-cancel>${t.t("common.cancel")}</button>
            </div>
          </form>
        </div>
      </div>`;
  }

  onMounted() {
    const close = () => this.unmount();
    this.listen(this.$(".modal-close"), "click", close);
    this.listen(this.$("[data-cancel]"), "click", close);
    this.listen(this.el, "click", (e) => {
      if (e.target === this.el) close();
    });
    this.listen(document, "keydown", (e) => {
      if (e.key === "Escape") close();
    });
    this.listen(this.$(".user-form"), "submit", (e) => this.submit(e));

    const del = this.$('[data-action="delete"]');
    if (del) this.listen(del, "click", () => this.remove());
  }

  async remove() {
    const u = this.props.user;
    if (!window.confirm(t.t("cockpit.users.confirmDelete").replace("{name}", u?.name || ""))) return;
    try {
      await api.del(`/api/users/${encodeURIComponent(u.userId)}`);
      this.props.onSaved?.();
      this.unmount();
    } catch (ex) {
      this.showError(this.$(".form-error"), ex.message || t.t("cockpit.users.errSave"));
    }
  }

  async submit(e) {
    e.preventDefault();
    const form = e.target;
    const err = this.$(".form-error");
    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const password = form.password.value;
    const admin = form.admin.value === "true";

    if (!name || !email) return this.showError(err, t.t("cockpit.users.errRequired"));
    if ((!this.props.user || password) && password.length < 12) return this.showError(err, t.t("cockpit.users.errPassword"));

    const body = { name, email, admin };
    if (password) body.password = password;

    try {
      if (this.props.user) await api.put(`/api/users/${encodeURIComponent(this.props.user.userId)}`, body);
      else await api.post("/api/users", body);
      this.props.onSaved?.();
      this.unmount();
    } catch (ex) {
      this.showError(err, ex.message || t.t("cockpit.users.errSave"));
    }
  }

  showError(el, msg) {
    el.textContent = msg;
    el.hidden = false;
  }
}
